export enum AssignmentType {
  LESSON_GUIDED = 'LESSON_GUIDED',
  INDEPENDENT = 'INDEPENDENT',
  CONTINUOUS = 'CONTINUOUS'
}

export enum StudentNotificationType {
  SUMMATIVE_ASSIGNED = 'summative_assigned',
  FORMATIVE_ASSIGNED = 'formative_assigned',
  LESSON_GUIDED_ASSIGNED = `${AssignmentType.LESSON_GUIDED}_assigned`,
  INDEPENDENT_ASSIGNED = `${AssignmentType.INDEPENDENT}_assigned`,
  CONTINUOUS_ASSIGNED = `${AssignmentType.CONTINUOUS}_assigned`,
  ASSESSMENTS_GRADED = 'assessments_graded'
}

export enum FormativeRowTypes {
  FORMATIVE_QUIZ = 'formative_quiz',
  LESSON_GUIDED_PRACTICE = 'lesson_guided_practice',
  INDEPENDENT_PRACTICE = 'independent_practice',
  CONTINUOUS_PRACTICE = 'continuous_practice'
}

export interface AssignmentQuestion {
  id: number
  question_text: string
  order_index: number // starts from 1
  content_html: string
  complete: boolean
  choices: {
    choice_1?: string
    choice_2?: string
    choice_3?: string
    choice_4?: string
  }
  answer: number
}

export enum PageLikeType {
  SECTION = 'section',
  QUESTION_SET = 'question_set',
  TEACHER_GUIDE_SECTION = 'teacher_guide_section'
}

enum TTSSourceTypes {
  CONTENT_BLOCK = 'content_block',
  TEACHER_BLOCK = 'teacher_block',
  STIMULUS_TAB_ONE = 'stimulus_tab_one',
  STIMULUS_TAB_TWO = 'stimulus_tab_two',
  QUESTION_TEXT = 'question_text',
  CHOICE_1 = 'choice_1',
  CHOICE_2 = 'choice_2',
  CHOICE_3 = 'choice_3',
  CHOICE_4 = 'choice_4'
}

export interface TTSKey {
  source_row_id: number // this is the body item id
  source_type: TTSSourceTypes // defines the type of content tts key is associated with
  paragraph_index: number // starts from 0
  audio_url: string // the audio url for generated file that we can play
}

export interface AssignmentPage {
  id: number
  assignment_id: number
  page_type: PageLikeType
  title: string
  order_index: number // starts from 1
  is_disabled: boolean
  student_page_number?: number
  student_pen_markings?: any
  student_pen_note_markings?: any
  student_notes: string
  is_bookmarked: boolean
  stimulus_tts_keys: TTSKey[]

  reference_section_id?: number | null
  reference_section_title: string | null
  page_range: string
  instructions_html?: string
  stimulus_tab_one?: string
  stimulus_tab_two?: string
  linked_page_id?: number
  linked_previous_qs_id?: number
  body_items: AssignmentBodyItem[] | QuestionBodyItem[]
}

export interface AssignmentBodyItem {
  id: number
  body_item_type: ContentBlockType
  content_html: string
  complete: boolean
  /**
   * We're following a pattern from backend, where each usable item for text to speech is marked with ID in html
   * ID will follow the following fingerprint
   *
   * tts-{source_row_id}-{source_type}-p{paragraph_index}
   *
   * Assuming a question text, it will have the following signature if there's no more than one paragraph
   * tts-123-question_text-p0
   *
   */
  tts_keys: TTSKey[]
  order_index: number // starts from 1
}

export type ContentBlockType =
  | 'teacher_block'
  | 'student_block'
  | 'content_block'
  | 'question'

export interface QuestionBodyItem extends AssignmentBodyItem {
  answer: number
  student_answer: number | string | null
  tts_keys: TTSKey[]
  question_text: string
  choices: {
    choice_1?: string
    choice_2?: string
    choice_3?: string
    choice_4?: string
  }
}

export interface TeacherbodyItem extends AssignmentBodyItem {}

export interface Assignment {
  id: number
  objective_id: number
  has_student_scores: boolean
  created_by_admin_id: number
  title: string
  type:
    | AssignmentType.LESSON_GUIDED
    | AssignmentType.INDEPENDENT
    | AssignmentType.CONTINUOUS
  is_published: boolean
  total_pages: number
  total_questions: number
  created_at: string
  updated_at: string
  pages: AssignmentPage[]
  toc: {
    main: MainTocItem[]
    questions: AssignmentQuestion[]
  }
}

export interface TocPageItem {
  id: number
  label: string
  has_notes: boolean
  is_bookmarked: boolean
  index: number
}

export interface MainTocItem {
  pages: TocPageItem[]
  student_page_range?: string // e.g. "7-9"
  page_questions?: PageQuestionItem[]
  title: string
  type: PageLikeType | 'INDIVIDUAL_QUESTIONS'
}

export interface PageQuestionItem {
  question_sets: number[]
  page_id: number
  has_notes: boolean
  is_bookmarked: boolean
  student_page_number?: number
}
